import { EventDefinitionType, LookupNoYesType, Permission, VisitDetailsForm as VisitDetailsFormDto } from "@/api/dtos";
import useBaselineWeightByPatient from "@/hooks/data/useBaselineWeightByPatient";
import { positiveWithPrecision, withPrecision } from "@/utils/NumberFormats";
import { ConditionMode, useFormActions, useFormState, ValidateOn } from "@ngt/forms-core";
import { CollaboratingGroupContext, CrfCondition, CrfForm, DatePicker, FormBreadcrumbs, GroupedField, ICrfConditionParameters, InstitutionContext, MasterGroupContext, Numeric, PageLayout, PatientContext, RouteLoading, Select, Text, ToggleButtonGroup } from "@ngt/opms";
import { crfReadOnly } from "@/utils/CrfFormUtils";
import { usePermissionsByIds } from "@ngt/opms-bctapi";
import { FC, useCallback, useContext, useEffect } from "react";
import LabelWithTooltip from "../labels/LabelWithTooltip";

interface IVisitDetailsFormProps {

}

interface IFormContentProps {

}

const CalculatedFieldContent: FC<IFormContentProps> = () => {
    const { setFieldValue } = useFormActions();
    const { value } = useFormState({ value : true});
    const {data: patient} = useContext(PatientContext);
    const {data: baselineWeight} = useBaselineWeightByPatient(patient?.id);

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            if(!value.weight || !baselineWeight) {
                setFieldValue("weightDifference", undefined);
                return;
            }
            
            setFieldValue("weightDifference", value.weight - baselineWeight);
        }, 500)

        return () => window.clearTimeout(timeout);
    }, [baselineWeight, value.weight]);

    return (
        <>
            
        </>
    )
}

const VisitDetailsForm: FC<IVisitDetailsFormProps> = () => {

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );
    
    const cycle2 = useCallback((params: ICrfConditionParameters<VisitDetailsFormDto>) => 
        params.eventDefinition?.id == EventDefinitionType.Cycle2
    , [])


    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout breadcrumbs={<FormBreadcrumbs />}>
            <CrfForm 
                validateOn={ValidateOn.onChange}
                readOnly={crfReadOnly(canUpdateCrf)}
                enableQueries
            >
                <CalculatedFieldContent />
                <GroupedField
                    propertyPath="visitDate"
                    format="dd-MMM-yyyy"
                    component={DatePicker}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
                <GroupedField 
                    propertyPath="weight"
                    component={Numeric}
                    numberFormat={positiveWithPrecision(1)}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
                <GroupedField
                    propertyPath="weightDifference"
                    component={Numeric}
                    numberFormat={withPrecision(1)}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                    disabled
                />
                <CrfCondition
                    condition={cycle2}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="dateStartTdxD"
                        component={DatePicker}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <GroupedField
                    propertyPath="nausea"
                    component={ToggleButtonGroup}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                    LabelProps={{
                        label: <LabelWithTooltip content="Only report treatment related Adverse Events" />
                    }}
                />
                <CrfCondition
                    condition={(params) => params.formState?.value.nausea == LookupNoYesType.Yes}
                    mode={ConditionMode.Enable}
                >
                    <GroupedField
                        propertyPath="nauseaGrade"
                        component={Select}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={(params) => params.formState?.value.nausea == LookupNoYesType.Yes}
                    mode={ConditionMode.Enable}
                >
                    <GroupedField
                        propertyPath="nauseaMedications"
                        component={Text}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <GroupedField
                    propertyPath="vomiting"
                    component={ToggleButtonGroup}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                    LabelProps={{
                        label: <LabelWithTooltip content="Only report treatment related Adverse Events" />
                    }}
                />
                <CrfCondition
                    condition={(params) => params.formState?.value.vomiting == LookupNoYesType.Yes}
                    mode={ConditionMode.Enable}
                >
                    <GroupedField
                        propertyPath="vomitingGrade"
                        component={Select}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={(params) => params.formState?.value.vomiting == LookupNoYesType.Yes}
                    mode={ConditionMode.Enable}
                >
                    <GroupedField
                        propertyPath="vomitingMedications"
                        component={Text}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <GroupedField
                    propertyPath="weightLoss"
                    component={ToggleButtonGroup}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                    LabelProps={{
                        label: <LabelWithTooltip content="Only report treatment related Adverse Events" />
                    }}
                />
                <CrfCondition
                    condition={(params) => params.formState?.value.weightLoss == LookupNoYesType.Yes}
                    mode={ConditionMode.Enable}
                >
                    <GroupedField
                        propertyPath="weightLossGrade"
                        component={Select}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={(params) => params.formState?.value.weightLoss == LookupNoYesType.Yes}
                    mode={ConditionMode.Enable}
                >
                    <GroupedField
                        propertyPath="weightLossMedications"
                        component={Text}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <GroupedField
                    propertyPath="anorexia"
                    component={ToggleButtonGroup}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                    LabelProps={{
                        label: <LabelWithTooltip content="Only report treatment related Adverse Events" />
                    }}
                />
                <CrfCondition
                    condition={(params) => params.formState?.value.anorexia == LookupNoYesType.Yes}
                    mode={ConditionMode.Enable}
                >
                    <GroupedField
                        propertyPath="anorexiaGrade"
                        component={Select}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={(params) => params.formState?.value.anorexia == LookupNoYesType.Yes}
                    mode={ConditionMode.Enable}
                >
                    <GroupedField
                        propertyPath="anorexiaMedications"
                        component={Text}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <GroupedField
                    propertyPath="otherAdverseEvents"
                    LabelProps={{label: <LabelWithTooltip content={"Include grade (0,1,2,3,4)"}/> as any}}
                    component={Text}
                    multiline
                    rows={4}
                    LabelGridProps={{
                        sx: {
                            alignSelf: "flex-start",
                            mt: 0.5
                        }
                    }}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
            </CrfForm>
        </PageLayout>
    );
}

export default VisitDetailsForm;